import React, { useLayoutEffect } from 'react';
import FormContainer from '../../../../Components/Form/FormContainer';
import SelectField from '../../../../Components/Form/components/SelectField';
import DateField from '../../../../Components/Form/components/DateField';
import InputField from '../../../../Components/Form/components/InputField';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import CSSZSickLeaveCertificateEmploymentModal from '../CSSZSickLeaveCertificateEmployment/CSSZSickLeaveCertificateEmploymentModal';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import CSSZAddressContainer from '../CSSZSickLeaveCertificateCreate/FormParts/CSSZAddressContainer';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DeepPartial } from '../../../../Interfaces/DeepPartial';
import { CSSZSickLeaveValues } from '../CSSZSickLeaveCertificateCreate/CSSZSickLeaveCertificateCreate';
import {
	CSSZEmployerData,
	CSSZEmployment,
} from '../../Interfaces/CSSZ.interface';
import TermItemAsyncSelectField from '../../../Base/Components/Fields/TermItemAsyncSelect/TermItemAsyncSelectField';
import {
	useLazyCsszEmploymentProfessionMetaQuery,
	useLazyTermItemByNameQuery,
} from '../../../../graphql/graphql';

interface Props {
	patientId: string;
	collectedValues?: DeepPartial<CSSZSickLeaveValues>;
	onSubmit: (values: CSSZEmployment) => void;
}

interface UserMeta {
	key?: string;
	value?: {
		employmentId?: string;
		profession?: string;
	};
}

const TYPE_OF_ADDRESS: SelectValue[] = [
	{
		label: 'Zaměstnavatel',
		value: 'employer',
	},
];

const getProfessionFromMeta = (
	metas: UserMeta[],
	employmentId: string,
): string | undefined => {
	if (!metas) {
		return undefined;
	}

	const employmentMeta = metas.find(
		meta => meta.value?.employmentId === employmentId,
	);
	return employmentMeta?.value?.profession;
};

const validationScheme = yup.object({
	employmentId: yup.string().nullable(),
	profession: yup
		.string()
		.required('validation.CSSZSickLeaveCertificateEmploymentForm.profession'),
	employer: yup
		.string()
		.required('validation.CSSZSickLeaveCertificateEmploymentForm.employer'),
	address: yup.object({
		street: yup.string().nullable(),
		descriptiveNumber: yup
			.string()
			.required(
				'validation.CSSZSickLeaveCertificateEmploymentForm.descriptiveNumber',
			),
		guideNumber: yup.string().nullable(),
		city: yup
			.string()
			.required('validation.CSSZSickLeaveCertificateEmploymentForm.city'),
		postCode: yup
			.string()
			.trim()
			.required('validation.CSSZSickLeaveCertificateEmploymentForm.postCode')
			.matches(
				/^\d{5}$/,
				'validation.CSSZSickLeaveCertificateEmploymentForm.postCodeMatch',
			),
		typeOfAddress: yup
			.object({
				value: yup.string(),
				label: yup.string(),
			})
			.nullable(),
		state: yup
			.object({
				value: yup
					.string()
					.required('validation.CSSZSickLeaveCertificateEmploymentForm.state'),
				label: yup.string(),
			})
			.required('validation.CSSZSickLeaveCertificateEmploymentForm.state')
			.typeError('validation.CSSZSickLeaveCertificateEmploymentForm.state'),
		floor: yup.string().nullable(),
		partOfCity: yup.string().nullable(),
		note: yup.string().nullable(),
	}),
	typeOfEmployment: yup
		.object({
			value: yup.string(),
			label: yup.string(),
		})
		.nullable()
		.optional(),
	validFrom: yup.string().nullable(),
	validTo: yup.string().nullable(),
	note: yup.string().nullable(),
});

const CSSZSickLeaveCertificateEmploymentForm: React.FC<Props> = ({
	onSubmit,
	patientId,
	collectedValues,
}) => {
	const { isOpen, onOpen, onClose: onClosePreSelection } = useDisclose();
	const [getTermItemByName] = useLazyTermItemByNameQuery();
	const [getCsszEmploymentProfessionMeta] =
		useLazyCsszEmploymentProfessionMetaQuery();

	useLayoutEffect(() => {
		onOpen();
	}, []);

	const formMethods = useForm({
		defaultValues: collectedValues?.employment,
		resolver: yupResolver(validationScheme),
	});

	const handleSelectEmployer = async (item: CSSZEmployerData) => {
		const [{ data: stateTermItem }, { data: metaData }] = await Promise.all([
			getTermItemByName({
				termType: 'cssz_ciselnik_zemi',
				name: item.address.country,
			}),
			getCsszEmploymentProfessionMeta({ userId: patientId }),
		]);

		const professionFromMeta = getProfessionFromMeta(
			metaData?.user?.meta || [],
			item.employment.id,
		);

		formMethods.reset({
			employmentId: item.employment.id,
			address: {
				street: item.address.street,
				descriptiveNumber: item.address.houseNumber,
				guideNumber: item.address.orientationNumber,
				postCode: item.address.postalCode,
				city: item.address.city,
				partOfCity: item.address.districtName,
				state: stateTermItem?.termItem
					? {
							label: stateTermItem.termItem.name,
							value: stateTermItem.termItem.id,
					  }
					: undefined,
			},
			validFrom: item.employment.startDate ?? undefined,
			validTo: item.employment.endDate ?? undefined,
			employer: item.employer.name,
			profession: professionFromMeta ?? '',
		});
		onClosePreSelection();
	};

	return (
		<>
			<FormWrapper formMethods={formMethods}>
				<div style={{ display: 'none' }}>
					<InputField name='employmentId' label='employmentId' isWrapped />
				</div>
				<InputField
					name='profession'
					label='component.CSSZSickLeaveCertificateEmploymentForm.profession'
					isRequired
					isWrapped
				/>
				<InputField
					name='employer'
					label='component.CSSZSickLeaveCertificateEmploymentForm.employer'
					isRequired
					isWrapped
				/>
				<FormContainer name='address'>
					<div className='row'>
						<div className='col-6'>
							<SelectField
								name='typeOfAddress'
								options={TYPE_OF_ADDRESS}
								label='component.CSSZSickLeaveCertificateEmploymentForm.typeOfAddress'
								isWrapped
							/>
						</div>
						<div className='col-6'>
							<TermItemAsyncSelectField
								name='state'
								label='component.CSSZSickLeaveCertificateEmploymentForm.state'
								termType='cssz_ciselnik_zemi'
								isRequired
								isWrapped
							/>
						</div>
					</div>
					<CSSZAddressContainer />
				</FormContainer>
				<TermItemAsyncSelectField
					name='typeOfEmployment'
					label='component.CSSZSickLeaveCertificateEmploymentForm.typeOfEmployment'
					termType='cssz_druh_zamestnani'
					isWrapped
				/>
				<div className='row'>
					<div className='col-6'>
						<DateField
							name='validFrom'
							label='component.CSSZSickLeaveCertificateEmploymentForm.validFrom'
							isWrapped
						/>
					</div>
					<div className='col-6'>
						<DateField
							name='validTo'
							label='component.CSSZSickLeaveCertificateEmploymentForm.validTo'
							isWrapped
						/>
					</div>
				</div>
				<InputField
					name='note'
					label='component.CSSZSickLeaveCertificateEmploymentForm.note'
				/>
				<div className='mt-3'>
					<SubmitButton
						title='component.CSSZSickLeaveCertificateEmploymentForm.save'
						onSubmit={onSubmit}
					/>
				</div>
			</FormWrapper>
			{isOpen && (
				<CSSZSickLeaveCertificateEmploymentModal
					isOpen
					onClose={onClosePreSelection}
					patientId={patientId}
					onSelect={handleSelectEmployer}
				/>
			)}
		</>
	);
};

export default CSSZSickLeaveCertificateEmploymentForm;
